// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { defineComponent } from '@shared/ui-kit/utils/define-component';
import {
  useBankAccountDrawer,
  type ProductSummaryData,
} from './use-bank-account-drawer';
import {
  Button,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  Typography,
  type ListItemProps,
  type ChipProps,
} from '@mui/material';
import { AppIcon } from '@shared/ui-kit/components/app-icon';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { AssetLogo, Table } from '@shared/ui-kit';
import { Translation } from '@shared/ui-kit/providers/translation-provider';
import { ShareStepEmailForm } from '@shared/advisory/aggregation/components/share-institutions-button/share-step-email-form';
import { useModal } from '@shared/ui-kit/hooks';
import { TagManagement } from '@shared/advisory/aggregation/components/tags';
import { TagObjectType } from '@shared/data-access/interfaces/redux/tags-management-state-types';
import { ShowInAppClient } from '@shared/ui-kit/utils/app-utils';

export const BankAccountDrawer = defineComponent({
  displayName: 'BankAccountDrawer',
  useSetup: useBankAccountDrawer,
  Slots: {
    Section: ({
      divider,
      className,
      ...props
    }: ListItemProps & { divider?: boolean }) => (
      <>
        <ListItem
          className={classNames('flex flex-col !items-start p-7', className)}
          disablePadding
          disableGutters
          {...props}
        >
          {props.children}
        </ListItem>
        {divider && <Divider />}
      </>
    ),
    Tag: (props: ChipProps) => (
      <Chip
        color="primary"
        variant="filled"
        size="small"
        deleteIcon={<AppIcon icon="Close" color="inherit" size="lg" />}
        {...props}
      />
    ),
    ProductSummary: ({
      summary,
      currency,
    }: {
      summary: ProductSummaryData | null;
      currency: string;
    }) => {
      if (!summary) return null;

      return (
        <dl className="grid grid-cols-2 gap-4 self-stretch text-base text-primary-medium">
          <dt className="flex items-center gap-2">
            <AppIcon icon={summary.icon} color="inherit" size="lg" />
            <Translation i18nKey="common.type" ns="common" />
          </dt>
          <dd className="text-end font-medium !text-title-dark">
            {summary.typeLabel}
          </dd>

          <dt>
            <Translation i18nKey="common.cash" ns="common" />
          </dt>
          <dd className="text-end font-medium !text-title-dark">
            <Table.Data
              value={summary.cash}
              format="currency"
              currency={currency}
              safeValue="-"
            />
          </dd>

          <dt>
            <Translation i18nKey="common.current-liquidity" ns="common" />
          </dt>
          <dd className="text-end font-medium !text-title-dark">
            <Table.Data
              value={summary.available}
              format="currency"
              currency={currency}
              safeValue="-"
            />
          </dd>

          <dt>
            <Translation i18nKey="glossary.debt" ns="glossary" />
          </dt>
          <dd className="text-end font-medium !text-title-dark">
            <Table.Data
              value={summary.debt}
              format="currency"
              currency={currency}
              safeValue="-"
            />
          </dd>

          <dt>
            <Translation i18nKey="common.investment" ns="common" />
          </dt>
          <dd className="text-end font-medium !text-title-dark">
            <Table.Data
              value={summary.investment}
              format="currency"
              currency={currency}
              safeValue="-"
            />
          </dd>

          <dt>
            <Translation
              i18nKey="aggregator.account-summary.ownership"
              ns="aggregator"
            />
          </dt>
          <dd className="text-end font-medium capitalize !text-title-dark">
            {summary.owners}
          </dd>
        </dl>
      );
    },
  },
  View: ({
    DrawerRenderer,
    onClose,
    Slots,
    product = null,
    institution = null,
    maskedIban,
    lastSyncFormatted,
    summary = null,
    currency,
    t,
    tagManagement,
    isShareOpen,
    email,
    setEmail,
    isSharing,
    advisors,
    isLoadingAdvisors,
    handleShareClose,
    handleShare,
  }) => {
    const [ModalRenderer, { onOpen, onClose: onCloseModal }] = useModal({
      component: 'dialog',
      defaultOpen: isShareOpen,
      afterClose: handleShareClose,
    });
    const handleShareAndClose = async (targetEmail?: string) => {
      await handleShare(targetEmail);
      onCloseModal();
    };

    return (
      <DrawerRenderer
        anchor="right"
        PaperProps={{
          classes: {
            root: '!bg-page !w-full !max-w-2xl',
          },
        }}
      >
        <List dense disablePadding className="!flex !flex-1 !flex-col">
          <Slots.Section divider className="relative !gap-4">
            <header className="flex gap-4">
              <div className="flex size-16 items-center justify-center rounded-lg bg-section">
                {institution?.logo_url ? (
                  <AssetLogo
                    logoUrl={institution?.logo_url}
                    className="size-full !object-contain"
                  />
                ) : (
                  <AppIcon icon="Building" color="disabled" size="lg" />
                )}
              </div>

              <div className="flex flex-col">
                <Typography variant="body2" className="!text-base !text-title-dark">
                  {institution?.name}
                </Typography>

                <Typography
                  variant="h6"
                  className="!text-xl !font-semibold !text-title-dark"
                >
                  {product?.name}
                </Typography>
              </div>

              <div className="absolute right-0 top-0 ml-auto p-7">
                <IconButton
                  aria-label={t('common.close', { ns: 'common' })}
                  size="small"
                  className="!rounded-lg"
                  onClick={onClose}
                  data-testid="close-button"
                >
                  <AppIcon icon="Close" color="inherit" />
                </IconButton>
              </div>
            </header>

            <div className="flex items-center justify-between gap-2 self-stretch">
              <Typography
                variant="body2"
                className="!money-display !text-base !text-primary-medium"
              >
                {maskedIban}
              </Typography>

              {lastSyncFormatted && (
                <Typography
                  variant="caption"
                  className="!text-base !font-medium !text-primary-green"
                >
                  <AppIcon
                    icon="CircleCheck"
                    color="inherit"
                    size="xl"
                    className="mr-2"
                  />
                  <span className="first-letter:!uppercase">
                    {lastSyncFormatted}
                  </span>
                </Typography>
              )}
            </div>
          </Slots.Section>

          <Slots.Section divider>
            <Typography
              variant="subtitle2"
              className="!mb-3 !text-base font-semibold uppercase !text-primary-medium"
            >
              <Translation
                i18nKey="aggregator.account-summary.title"
                ns="aggregator"
              />
            </Typography>

            <Slots.ProductSummary summary={summary} currency={currency} />
          </Slots.Section>

          {product?.iban && tagManagement && (
            <Slots.Section divider className="gap-2">
              <TagManagement
                objectType={TagObjectType.Account}
                objectId={product.iban}
                allTags={tagManagement.tags}
                initialAssignedTags={tagManagement.initialAssignedTags}
                onChange={tagManagement.onChangeTagIds}
                onCreateTag={tagManagement.onCreateTag}
              />
            </Slots.Section>
          )}

          <Slots.Section divider className="flex-grow"></Slots.Section>

          <ShowInAppClient>
            <Slots.Section className="items-center justify-stretch gap-2">
              <Button
                variant="contained"
                color="secondary"
                startIcon={<AppIcon icon="Share" color="inherit" size="lg" />}
                fullWidth
                onClick={onOpen}
              >
                <Translation i18nKey="aggregator.share-advisor" ns="aggregator" />
              </Button>
              <ModalRenderer
                {...({ onClose: onCloseModal } as any)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '& .MuiBackdrop-root': {
                    backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  },
                  '& .MuiPaper-root': {
                    position: 'relative',
                    backgroundColor: 'var(--bg-page)',
                    width: '600px !important',
                    maxWidth: '600px !important',
                    borderRadius: '12px',
                    border: '1px solid rgba(0, 0, 0, 0.25)',
                    margin: 'auto',
                    padding: '8px',
                    boxShadow: 'none',
                  },
                }}
              >
                <div className="relative p-4">
                  <div className="absolute right-6 top-6">
                    <IconButton
                      onClick={onCloseModal}
                      size="small"
                      className="!text-primary-medium"
                    >
                      <AppIcon icon="Close" size="xl" />
                    </IconButton>
                  </div>
                  <div className="flex flex-col items-start">
                    <Typography
                      variant="h6"
                      className="!mb-1 !font-bold !text-title-dark"
                    >
                      <Translation
                        i18nKey="aggregator.share-step-2-title"
                        ns="aggregator"
                      />
                    </Typography>
                    <Typography
                      variant="body2"
                      className="!mb-6 !text-primary-medium"
                    >
                      <Translation
                        i18nKey="aggregator.share-accounts-count-one"
                        ns="aggregator"
                        values={{ count: 1 }}
                      />
                    </Typography>
                  </div>
                  <ShareStepEmailForm
                    email={email}
                    setEmail={setEmail}
                    onShare={handleShareAndClose}
                    isSharing={isSharing}
                    advisors={advisors}
                    isLoadingAdvisors={isLoadingAdvisors}
                  />
                </div>
              </ModalRenderer>
            </Slots.Section>
          </ShowInAppClient>
        </List>
      </DrawerRenderer>
    );
  },
});
